import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    CartLineModel,
    ProductSettingsModel,
    PromotionModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import getProductCategories from "@waytek/Handlers/Utilities/CustomGetProductCategories";
import { trackRemoveFromCartEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@waytek/Overrides/Widgets/Cart/CartLineCardCondensed";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@waytek/Overrides/Widgets/Cart/CartLineCardExpanded";
import removeCartBucket from "@waytek/Store/Data/Carts/Handlers/RemoveCartBucket";
import updateCartLine from "@waytek/Store/Pages/Cart/Handlers/UpdateCartLineApiResult";
import { loadBucketsItems } from "@waytek/Utilities/global/cart";
import { mediaQueriesBP } from "@waytek/Utilities/variables";
import CartLinesScheduleShipmentModal from "@waytek/Widgets/Cart/CartLinesScheduleShipmentModal";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    condensedViewAsDefault = "condensedViewAsDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.condensedViewAsDefault]?: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,

    /* Verndale Custom Feature */
    cartLoadedTime: state.data.carts.cartLoadedTime,
    customSettings: getSettingsCollection(state).customSettings as any,
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
    /* Verndale Custom Feature */
    clearCartLines: makeHandlerChainAwaitable(removeCartBucket as any),
    getProductCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    footer?: CartLinesFooterStyles;
}

interface CartLinesHeaderStyles {
    itemCountText?: TypographyProps;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainSection?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

interface CartLinesFooterStyles {
    mainSection?: InjectableCss;
    condensedViewCheckboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedViewCheckbox?: CheckboxPresentationProps;
}

export const cartLinesStyles: CartLinesStyles = {
    headerWrapper: {
        css: css`
            border-bottom: 6px solid ${getColor("common.global.industrialTaupe")};
            padding-bottom: 2px;
            min-height: 40px;

            ${mediaQueriesBP.minLG} {
                padding-bottom: 4px;
            }

            > section:not(.cart-line__warning-message) {
                padding-bottom: 0;
            }

            .cart-line__warning-message {
                margin: 10px 0;
            }
        `,
    },
    wrapper: {
        css: css`
            padding-bottom: 34px;

            ${mediaQueriesBP.minMD} {
                padding-bottom: 29px;
            }
        `,
    },
    header: {
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
        itemCountText: {
            css: css`
                font-size: 16px;
                font-weight: bold;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.5;
                letter-spacing: normal;
                color: ${getColor("common.global.secondaryGray")};
            `,
        },
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    margin-left: 0;

                    ${mediaQueriesBP.minMDMaxLg} {
                        z-index: 0;
                    }
                `,
            },
        },
        removeAllLink: {
            css: css`
                margin-left: 24px;
            `,
        },
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: "danger.main",
            css: css`
                margin-right: 10px;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${getColor("danger.main")};
                padding: 15px 10px;
            `,
        },
    },
    footerWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    footer: {
        mainSection: {
            css: css`
                display: flex;
                align-items: center;
                padding-bottom: 5px;
            `,
        },
        condensedViewCheckboxGroup: {
            css: css`
                margin-left: auto;
            `,
        },
        condensedViewCheckbox: {
            typographyProps: {
                css: css`
                    margin-left: 10px;
                `,
            },
        },
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields: { showLineNotes, condensedViewAsDefault },
    isClearingCart,
    isLoading,
    updateCartLine: updateCartLineHandler,
    removeCartLine: removeCartLineHandler,

    /* Verndale Custom Feature */
    cartLoadedTime,
    clearCartLines: clearCartLinesHandler,
    getProductCategories,
    customSettings,
}) => {
    const [isCondensedShippingNow, setIsCondensedShippingNow] = useState(condensedViewAsDefault ?? true);
    const [isCondensedPartialShipment, setIsCondensedPartialShipment] = useState(condensedViewAsDefault ?? true);
    const [isCondensedBackordered, setIsCondensedBackordered] = useState(condensedViewAsDefault ?? true);
    const [isCondensedScheduledShipments, setIsCondensedScheduledShipments] = useState(condensedViewAsDefault ?? true);
    const [cartLineSelected, setCartLineSelected] = useState<CartLineModel>();
    const [scheduleShipmentOpen, setScheduleShipmentOpen] = useState(false);
    const [scheduledShipmentsQty, setScheduledShipmentsQty] = useState(1);
    const [scheduledShipments, setScheduledShipments] = useState<any[]>([]);
    const [newCustomerNumber, setNewCustomerNumber] = useState<{ [key: string]: string }>({});

    const cartCondition = !cart || (cart.lineCount && !cart.cartLines) || isClearingCart;
    if (cartCondition || cart == null) {
        return null;
    }

    const cartLines = cart.cartLines || [];

    if (cartLines.length === 0) {
        return null;
    }

    const { shippingNowList, partialShipmentList, backorderedList, scheduledShipmentsList } =
        loadBucketsItems<CartLineModel>(cartLines);

    const isCondensedShippingNowChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensedShippingNow(value);
    };

    const isCondensedPartialShipmentChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensedPartialShipment(value);
    };

    const isCondensedBackorderedChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensedBackordered(value);
    };

    const isCondensedScheduledShipmentsChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsCondensedScheduledShipments(value);
    };

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;

    const handleScheduleShipmentOpen = (cartLine: CartLineModel) => {
        setCartLineSelected(cartLine);
        const qty = cartLine.qtyOrdered || 1;
        setScheduledShipmentsQty(qty);
        if (cartLine.properties["scheduledShipments"] && cartLine.properties["scheduledShipments"].length > 0) {
            const shipments: any[] = JSON.parse(cartLine.properties["scheduledShipments"]);
            if (shipments.length < 12) {
                shipments.push({
                    qty: 0,
                    date: null,
                    disabled: true,
                });
            }
            setScheduledShipments(shipments);
        } else {
            setScheduledShipments([
                {
                    qty,
                    date: null,
                    disabled: false,
                },
                {
                    qty: 0,
                    date: null,
                    disabled: true,
                },
            ]);
        }
        setScheduleShipmentOpen(true);
    };

    const handleScheduleShipmentClose = () => {
        setScheduleShipmentOpen(false);
        setCartLineSelected(undefined);
    };

    const localOnQtyOrderedChange = (inputValue: string) => {
        const inputQty = parseFloat(inputValue);
        if (Number.isNaN(inputQty)) {
            return;
        }

        setScheduledShipmentsQty(inputQty);
        const tmpValues = [...scheduledShipments];
        tmpValues[0].qty = inputQty;
        tmpValues.forEach((item, idx) => {
            if (idx > 0) {
                item.qty = 0;
            }
        });
        setScheduledShipments(tmpValues);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        scheduledShipmentsQty.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const handleScheduledShipmentChange = (item: any, idx: number) => {
        const tmpValues = [...scheduledShipments];
        tmpValues[idx] = item;
        setScheduledShipments(tmpValues);
    };

    const handleScheduleShipmentSave = () => {
        const scheduledShipmentsTmp = [...scheduledShipments];
        if (scheduledShipmentsTmp[scheduledShipmentsTmp.length - 1].qty === 0) {
            scheduledShipmentsTmp.pop();
        }
        const cartLineUpdated = {
            ...cartLineSelected,
            notes: cartLineSelected?.notes.slice(0, customSettings.notesMaxNumberOfCharacters),
            qtyOrdered: scheduledShipmentsQty,
            properties: {
                scheduledShipments: JSON.stringify(scheduledShipmentsTmp),
            },
        } as CartLineModel;

        if (cartLineSelected) {
            for (const key in cartLineSelected.properties) {
                if (key !== "multipleSaleQty" && key !== "roundingRule" && key !== "scheduledShipments") {
                    cartLineUpdated.properties[key] = cartLineSelected.properties[key];
                }
            }
        }

        updateCartLineHandler({
            cartLine: cartLineUpdated,
            onSuccess: () => {
                handleScheduleShipmentClose();
            },
        });
    };

    const handleAddRemoveShipmentLine = (idx: number, isAdd: boolean) => {
        const tmpValues = [...scheduledShipments];
        if (isAdd) {
            const newItem = {
                qty: 0,
                date: null,
                disabled: false,
            };
            if (idx === 0) {
                tmpValues[1].disabled = true;
                tmpValues.splice(idx + 1, 0, newItem);
            } else {
                newItem.disabled = tmpValues.length !== 11;
                tmpValues[tmpValues.length - 1].disabled = false;
                tmpValues.push(newItem);
            }
        } else {
            tmpValues.splice(idx, 1);
            tmpValues[tmpValues.length - 1].qty = 0;
            tmpValues[tmpValues.length - 1].disabled = true;
        }

        setScheduledShipments(tmpValues);
    };

    const customerChangeNumberHandler = (id: string, customerNumber: string) => {
        const newCustomerNumbers = { ...newCustomerNumber };
        newCustomerNumbers[id] = customerNumber;
        setNewCustomerNumber(newCustomerNumbers);
    };

    return (
        <>
            <CartLinesGroup
                cart={cart}
                lines={shippingNowList}
                isCondensed={isCondensedShippingNow}
                updateCartLineHandler={updateCartLineHandler}
                removeCartLineHandler={removeCartLineHandler}
                clearCartLinesHandler={clearCartLinesHandler}
                handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                productSettings={productSettings}
                promotions={promotions}
                showLineNotes={showLineNotes}
                type="shippingNow"
                bucket="shippingnow"
                isCondensedChangeHandler={isCondensedShippingNowChangeHandler}
                customerChangeNumberHandler={customerChangeNumberHandler}
                newCustomerNumber={newCustomerNumber}
                getProductCategories={getProductCategories}
            ></CartLinesGroup>

            <CartLinesGroup
                cart={cart}
                lines={partialShipmentList}
                isCondensed={isCondensedPartialShipment}
                updateCartLineHandler={updateCartLineHandler}
                removeCartLineHandler={removeCartLineHandler}
                clearCartLinesHandler={clearCartLinesHandler}
                handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                productSettings={productSettings}
                promotions={promotions}
                showLineNotes={showLineNotes}
                type="partialShipment"
                bucket="partialShipment"
                isCondensedChangeHandler={isCondensedPartialShipmentChangeHandler}
                customerChangeNumberHandler={customerChangeNumberHandler}
                newCustomerNumber={newCustomerNumber}
                getProductCategories={getProductCategories}
            ></CartLinesGroup>

            <CartLinesGroup
                cart={cart}
                lines={backorderedList}
                isCondensed={isCondensedBackordered}
                updateCartLineHandler={updateCartLineHandler}
                removeCartLineHandler={removeCartLineHandler}
                clearCartLinesHandler={clearCartLinesHandler}
                handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                productSettings={productSettings}
                promotions={promotions}
                showLineNotes={showLineNotes}
                type="backordered"
                bucket="backordered"
                isCondensedChangeHandler={isCondensedBackorderedChangeHandler}
                customerChangeNumberHandler={customerChangeNumberHandler}
                newCustomerNumber={newCustomerNumber}
                getProductCategories={getProductCategories}
            ></CartLinesGroup>

            <CartLinesGroup
                cart={cart}
                lines={scheduledShipmentsList}
                isCondensed={isCondensedScheduledShipments}
                updateCartLineHandler={updateCartLineHandler}
                removeCartLineHandler={removeCartLineHandler}
                clearCartLinesHandler={clearCartLinesHandler}
                handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                productSettings={productSettings}
                promotions={promotions}
                showLineNotes={showLineNotes}
                type="scheduledShipments"
                bucket="scheduleshipment"
                isCondensedChangeHandler={isCondensedScheduledShipmentsChangeHandler}
                customerChangeNumberHandler={customerChangeNumberHandler}
                newCustomerNumber={newCustomerNumber}
                getProductCategories={getProductCategories}
            ></CartLinesGroup>

            {scheduleShipmentOpen && (
                <CartLinesScheduleShipmentModal
                    key="CartLinesScheduledShipments"
                    cart={cart}
                    cartLine={cartLineSelected}
                    modalIsOpen={scheduleShipmentOpen}
                    handleModalClose={handleScheduleShipmentClose}
                    handleModalSave={handleScheduleShipmentSave}
                    cartLineQty={value}
                    handleCartLineChange={changeHandler}
                    handleCartLineKeyDown={keyDownHandler}
                    handleCartLineBlur={blurHandler}
                    shipments={scheduledShipments}
                    handleShipmentChange={handleScheduledShipmentChange}
                    handleAddRemoveShipmentLine={handleAddRemoveShipmentLine}
                ></CartLinesScheduleShipmentModal>
            )}
        </>
    );
};

interface CartLinesHeaderProps {
    totalItemCount: number;
    itemsType: string;
    isCondensed: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    hasInsufficientInventory?: boolean;
    hasInvalidPrice?: boolean;
    failedToGetRealTimeInventory?: boolean;
    onIsCondensedChange: CheckboxProps["onChange"];
    onRemoveAllClick: () => void;
}

const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    itemsType,
    isCondensed,
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    onIsCondensedChange,
    onRemoveAllClick,
}) => {
    const headerStyles = styles.header || {};

    let translationKey = "";
    switch (itemsType) {
        case "shippingNow":
            translationKey = "Shipping Now ({0})";
            break;
        case "partialShipment":
            translationKey = "Partial Shipment ({0})";
            break;
        case "backordered":
            translationKey = "Backordered ({0})";
            break;
        case "scheduledShipments":
            translationKey = "Scheduled Shipments ({0})";
            break;
    }
    const groupTranslated = translate(translationKey, `${totalItemCount}`);

    let translationKeyForCheckbox = "";
    switch (itemsType) {
        case "shippingNow":
            translationKeyForCheckbox = "Shipping Now";
            break;
        case "partialShipment":
            translationKeyForCheckbox = "Partial Shipment";
            break;
        case "backordered":
            translationKeyForCheckbox = "Backordered";
            break;
        case "scheduledShipments":
            translationKeyForCheckbox = "Scheduled Shipments";
            break;
    }
    const groupTranslatedForCheckbox = translate(translationKeyForCheckbox);

    return (
        <StyledSection {...styles.headerWrapper} id="cartHeader">
            <StyledSection {...headerStyles.mainSection}>
                <Typography {...headerStyles.itemCountText}>{groupTranslated}</Typography>

                <CheckboxGroup {...headerStyles.condensedViewCheckboxGroup}>
                    <Checkbox
                        checked={isCondensed}
                        onChange={onIsCondensedChange}
                        data-test-selector="cartlineHeader_condensedCheckbox"
                        {...headerStyles.condensedViewCheckbox}
                    >
                        {parse(translate("Condensed View for <b>{0}</b>", groupTranslatedForCheckbox), parserOptions)}
                        <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                    </Checkbox>
                </CheckboxGroup>
                <Hidden above="md">
                    <OverflowMenu position="end" {...headerStyles.overflowMenu}>
                        <Clickable onClick={onRemoveAllClick}>{translate("Remove All")}</Clickable>
                    </OverflowMenu>
                </Hidden>
                <Hidden below="lg">
                    <Link
                        onClick={onRemoveAllClick}
                        {...headerStyles.removeAllLink}
                        data-test-selector="cartlineHeader_removeAll"
                    >
                        {translate("Remove All")}
                    </Link>
                </Hidden>
            </StyledSection>
            {productsCannotBePurchased && (
                <StyledSection {...headerStyles.warningSection} className="cart-line__warning-message">
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                    >
                        {siteMessage("Cart_ProductsCannotBePurchased")}
                    </Typography>
                </StyledSection>
            )}
            {cartNotPriced && (
                <StyledSection {...headerStyles.warningSection} className="cart-line__warning-message">
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                    >
                        {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {hasInsufficientInventory && (
                <StyledSection {...headerStyles.warningSection} className="cart-line__warning-message">
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography
                        {...headerStyles.warningText}
                        data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                    >
                        {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <StyledSection {...headerStyles.warningSection} className="cart-line__warning-message">
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>{siteMessage("Cart_InvalidPriceAtCheckout")}</Typography>
                </StyledSection>
            )}
            {failedToGetRealTimeInventory && (
                <StyledSection {...headerStyles.warningSection} className="cart-line__warning-message">
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
        </StyledSection>
    );
};

interface CartLinesFooterProps {
    itemsType: string;
    isCondensed: boolean;
    onIsCondensedChange: CheckboxProps["onChange"];
}

const CartLinesFooter: FC<CartLinesFooterProps> = ({ itemsType, isCondensed, onIsCondensedChange }) => {
    const footerStyles = styles.footer || {};
    let translationKey = "";
    switch (itemsType) {
        case "shippingNow":
            translationKey = "Shipping Now";
            break;
        case "partialShipment":
            translationKey = "Partial Shipment";
            break;
        case "backordered":
            translationKey = "Backordered";
            break;
        case "scheduledShipments":
            translationKey = "Scheduled Shipments";
            break;
    }
    const groupTranslated = translate(translationKey);

    return (
        <StyledSection {...styles.footerWrapper} id={`cartFooter_${itemsType}`}>
            <StyledSection {...footerStyles.mainSection}>
                <CheckboxGroup {...footerStyles.condensedViewCheckboxGroup}>
                    <Checkbox
                        checked={isCondensed}
                        onChange={onIsCondensedChange}
                        data-test-selector="cartlineHeader_condensedCheckbox"
                        {...footerStyles.condensedViewCheckbox}
                    >
                        {parse(translate("Condensed View for <b>{0}</b>", groupTranslated), parserOptions)}
                        <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                    </Checkbox>
                </CheckboxGroup>
            </StyledSection>
        </StyledSection>
    );
};

const CartLinesGroup: FC<{
    cart: Cart;
    lines: CartLineModel[];
    isCondensed: boolean;
    updateCartLineHandler: any;
    removeCartLineHandler: any;
    clearCartLinesHandler: any;
    handleScheduleShipmentOpen: (cartLine: CartLineModel) => void;
    productSettings: ProductSettingsModel;
    promotions?: PromotionModel[];
    showLineNotes: boolean;
    type: string;
    isCondensedChangeHandler: (_: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    customerChangeNumberHandler: (id: string, customerNumber: string) => void;
    newCustomerNumber: { [key: string]: string };
    bucket: string;
    getProductCategories: any;
}> = ({
    cart,
    lines,
    isCondensed,
    updateCartLineHandler,
    removeCartLineHandler,
    clearCartLinesHandler,
    handleScheduleShipmentOpen,
    productSettings,
    promotions,
    showLineNotes,
    type,
    isCondensedChangeHandler,
    customerChangeNumberHandler,
    newCustomerNumber,
    bucket,
    getProductCategories,
}) => {
    if (lines.length === 0) {
        return null;
    }

    const removeAllClickHandler = () => {
        const cartLines = cart.cartLines ?? [];
        clearCartLinesHandler({ cartId: cart.id, bucket });

        /* Verndale Custom Feature - START */
        const productIds = lines.map(o => o.productId) as string[];
        getProductCategories({
            ProductIds: productIds,
            onComplete: (result: any) => {
                if (result && result.apiResult) {
                    trackRemoveFromCartEvent(cartLines, result.apiResult.productProperties);
                }
            },
        });
        /* Verndale Custom Feature - END */
    };

    const productsCannotBePurchased = lines.some(o => o.isRestricted || !o.isActive || o.cantBuy);

    const cartLinesDisplay = lines.map(cartLine => {
        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;

        return (
            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                {isCondensed ? (
                    <CartLineCardCondensed
                        cart={cart}
                        promotions={matchingPromotions}
                        productSettings={productSettings}
                        showInventoryAvailability={true}
                        showLineNotes={showLineNotes}
                        extendedStyles={styles.cardCondensed}
                        updateCartLine={updateCartLineHandler}
                        removeCartLine={removeCartLineHandler}
                        showRemoveAction={showRemoveAction}
                        handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                        bucketType={type}
                    />
                ) : (
                    <CartLineCardExpanded
                        cart={cart}
                        promotions={matchingPromotions}
                        productSettings={productSettings}
                        showInventoryAvailability={true}
                        showLineNotes={showLineNotes}
                        extendedStyles={styles.cardExpanded}
                        updateCartLine={updateCartLineHandler}
                        removeCartLine={removeCartLineHandler}
                        showRemoveAction={showRemoveAction}
                        handleScheduleShipmentOpen={handleScheduleShipmentOpen}
                        bucketType={type}
                        onCustomerNumberChange={customerNumber =>
                            customerChangeNumberHandler(cartLine.id, customerNumber)
                        }
                        newCustomerNumber={newCustomerNumber[cartLine.id]}
                    />
                )}
            </CartLineContext.Provider>
        );
    });

    return (
        <StyledSection {...styles.wrapper}>
            <CartLinesHeader
                totalItemCount={lines.length}
                itemsType={type}
                isCondensed={isCondensed}
                productsCannotBePurchased={productsCannotBePurchased}
                cartNotPriced={cart.cartNotPriced}
                hasInsufficientInventory={cart.hasInsufficientInventory && cart.fulfillmentMethod === "Ship"}
                hasInvalidPrice={hasProductsWithInvalidPrice(cart)}
                failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                onIsCondensedChange={isCondensedChangeHandler}
                onRemoveAllClick={removeAllClickHandler}
            />
            {cartLinesDisplay}
            <CartLinesFooter
                itemsType={type}
                isCondensed={isCondensed}
                onIsCondensedChange={isCondensedChangeHandler}
            />
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CartLines) as any,
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
            },
            {
                name: fields.condensedViewAsDefault,
                displayName: "Show condensed view by default?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
